/**
 * Targeting Rule Builder Types
 * 
 * UI-specific types for the targeting rule editor
 * Maps to backend DSL types but with UI enhancements
 * 
 * Phase 5.2: Types defined locally (backend-specific)
 */

// Core targeting types (from backend Phase 5.1)
export interface TargetingRuleV1 {
  version: "1";
  rule: Condition;
}

export type Condition =
  | LogicalCondition
  | CustomerCondition
  | CartCondition
  | ProductCondition
  | GeoCondition;

export interface LogicalCondition {
  type: "and" | "or" | "not";
  conditions: Condition[];
}

export type CustomerCondition =
  | CustomerTagsCondition
  | CustomerEmailCondition
  | CustomerOrdersCountCondition
  | CustomerTotalSpentCondition
  | CustomerIsNewCondition;

export interface CustomerTagsCondition {
  type: "customer_tags";
  operator: "contains" | "not_contains";
  value: string;
}

export interface CustomerEmailCondition {
  type: "customer_email";
  operator: "equals" | "contains" | "ends_with";
  value: string;
}

export interface CustomerOrdersCountCondition {
  type: "customer_orders_count";
  operator: "eq" | "ne" | "gt" | "gte" | "lt" | "lte";
  value: number;
}

export interface CustomerTotalSpentCondition {
  type: "customer_total_spent";
  operator: "gt" | "gte" | "lt" | "lte";
  value: number;
}

export interface CustomerIsNewCondition {
  type: "customer_is_new";
  value: boolean;
}

export type CartCondition =
  | CartSubtotalCondition
  | CartItemsCountCondition
  | CartContainsProductCondition
  | CartContainsCollectionCondition;

export interface CartSubtotalCondition {
  type: "cart_subtotal";
  operator: "gt" | "gte" | "lt" | "lte";
  value: number;
}

export interface CartItemsCountCondition {
  type: "cart_items_count";
  operator: "eq" | "ne" | "gt" | "gte" | "lt" | "lte";
  value: number;
}

export interface CartContainsProductCondition {
  type: "cart_contains_product";
  productId: string;
}

export interface CartContainsCollectionCondition {
  type: "cart_contains_collection";
  collectionId: string;
}

export type ProductCondition =
  | ProductIdCondition
  | ProductVendorCondition
  | ProductTypeCondition
  | ProductTagsCondition;

export interface ProductIdCondition {
  type: "product_id";
  operator: "equals" | "not_equals";
  value: string;
}

export interface ProductVendorCondition {
  type: "product_vendor";
  operator: "equals" | "not_equals" | "contains";
  value: string;
}

export interface ProductTypeCondition {
  type: "product_type";
  operator: "equals" | "not_equals";
  value: string;
}

export interface ProductTagsCondition {
  type: "product_tags";
  operator: "contains" | "not_contains";
  value: string;
}

export type GeoCondition = GeoCountryCondition | GeoRegionCondition;

export interface GeoCountryCondition {
  type: "geo_country";
  operator: "equals" | "not_equals";
  value: string;
}

export interface GeoRegionCondition {
  type: "geo_region";
  operator: "equals" | "not_equals";
  countryCode: string;
  value: string;
}

export interface EvaluationContext {
  customer?: {
    id: string | null;
    email: string | null;
    tags: string[];
    ordersCount: number;
    totalSpent: number;
    isNew: boolean;
  };
  cart?: {
    subtotal: number;
    itemsCount: number;
    items: Array<{
      productId: string;
      variantId: string;
      collectionIds: string[];
      quantity: number;
      price: number;
    }>;
  };
  product?: {
    id: string;
    vendor: string;
    type: string;
    tags: string[];
  };
  geo?: {
    countryCode: string | null;
    regionCode: string | null;
  };
  session: {
    shopId: string;
    playerId: string;
    deviceId: string;
    timestamp: Date;
  };
}

export interface EvaluationResult {
  matched: boolean;
  explanation: string;
  evaluatedConditions: number;
  shortCircuited: boolean;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

export interface ValidationError {
  path: string;
  message: string;
  code: string;
}

/**
 * Condition metadata for UI
 */
export interface ConditionMetadata {
  type: string;
  label: string;
  category: "customer" | "cart" | "product" | "geo";
  operators: Array<{
    value: string;
    label: string;
  }>;
  valueType: "string" | "number" | "boolean" | "productId" | "collectionId" | "countryCode";
  description: string;
}

/**
 * All available condition types with metadata
 */
export const CONDITION_TYPES: ConditionMetadata[] = [
  // Customer conditions
  {
    type: "customer_tags",
    label: "Customer has tag",
    category: "customer",
    operators: [
      { value: "contains", label: "contains" },
      { value: "not_contains", label: "does not contain" },
    ],
    valueType: "string",
    description: "Target customers with specific tags",
  },
  {
    type: "customer_email",
    label: "Customer email",
    category: "customer",
    operators: [
      { value: "equals", label: "equals" },
      { value: "contains", label: "contains" },
      { value: "ends_with", label: "ends with" },
    ],
    valueType: "string",
    description: "Target customers by email pattern",
  },
  {
    type: "customer_orders_count",
    label: "Customer orders count",
    category: "customer",
    operators: [
      { value: "eq", label: "equals" },
      { value: "ne", label: "not equals" },
      { value: "gt", label: "greater than" },
      { value: "gte", label: "greater than or equal" },
      { value: "lt", label: "less than" },
      { value: "lte", label: "less than or equal" },
    ],
    valueType: "number",
    description: "Target by number of previous orders",
  },
  {
    type: "customer_total_spent",
    label: "Customer total spent",
    category: "customer",
    operators: [
      { value: "gt", label: "greater than" },
      { value: "gte", label: "greater than or equal" },
      { value: "lt", label: "less than" },
      { value: "lte", label: "less than or equal" },
    ],
    valueType: "number",
    description: "Target by lifetime customer value",
  },
  {
    type: "customer_is_new",
    label: "Customer is new",
    category: "customer",
    operators: [],
    valueType: "boolean",
    description: "Target first-time customers",
  },
  
  // Cart conditions
  {
    type: "cart_subtotal",
    label: "Cart subtotal",
    category: "cart",
    operators: [
      { value: "gt", label: "greater than" },
      { value: "gte", label: "greater than or equal" },
      { value: "lt", label: "less than" },
      { value: "lte", label: "less than or equal" },
    ],
    valueType: "number",
    description: "Target by cart value",
  },
  {
    type: "cart_items_count",
    label: "Cart items count",
    category: "cart",
    operators: [
      { value: "eq", label: "equals" },
      { value: "ne", label: "not equals" },
      { value: "gt", label: "greater than" },
      { value: "gte", label: "greater than or equal" },
      { value: "lt", label: "less than" },
      { value: "lte", label: "less than or equal" },
    ],
    valueType: "number",
    description: "Target by number of items in cart",
  },
  {
    type: "cart_contains_product",
    label: "Cart contains product",
    category: "cart",
    operators: [],
    valueType: "productId",
    description: "Target when specific product is in cart",
  },
  {
    type: "cart_contains_collection",
    label: "Cart contains collection",
    category: "cart",
    operators: [],
    valueType: "collectionId",
    description: "Target when products from collection are in cart",
  },
  
  // Product conditions
  {
    type: "product_id",
    label: "Product ID",
    category: "product",
    operators: [
      { value: "equals", label: "equals" },
      { value: "not_equals", label: "not equals" },
    ],
    valueType: "productId",
    description: "Target specific product",
  },
  {
    type: "product_vendor",
    label: "Product vendor",
    category: "product",
    operators: [
      { value: "equals", label: "equals" },
      { value: "not_equals", label: "not equals" },
      { value: "contains", label: "contains" },
    ],
    valueType: "string",
    description: "Target by product vendor",
  },
  {
    type: "product_type",
    label: "Product type",
    category: "product",
    operators: [
      { value: "equals", label: "equals" },
      { value: "not_equals", label: "not equals" },
    ],
    valueType: "string",
    description: "Target by product type",
  },
  {
    type: "product_tags",
    label: "Product has tag",
    category: "product",
    operators: [
      { value: "contains", label: "contains" },
      { value: "not_contains", label: "does not contain" },
    ],
    valueType: "string",
    description: "Target products with specific tags",
  },
  
  // Geo conditions
  {
    type: "geo_country",
    label: "Country",
    category: "geo",
    operators: [
      { value: "equals", label: "equals" },
      { value: "not_equals", label: "not equals" },
    ],
    valueType: "countryCode",
    description: "Target by country (ISO 2-letter code)",
  },
  {
    type: "geo_region",
    label: "Region/State",
    category: "geo",
    operators: [
      { value: "equals", label: "equals" },
      { value: "not_equals", label: "not equals" },
    ],
    valueType: "string",
    description: "Target by region/state within country",
  },
];

/**
 * Get metadata for a condition type
 */
export function getConditionMetadata(type: string): ConditionMetadata | undefined {
  return CONDITION_TYPES.find((c) => c.type === type);
}

/**
 * Get all condition types for a category
 */
export function getConditionsByCategory(
  category: "customer" | "cart" | "product" | "geo"
): ConditionMetadata[] {
  return CONDITION_TYPES.filter((c) => c.category === category);
}

/**
 * Logical operator metadata
 */
export const LOGICAL_OPERATORS = [
  {
    value: "and",
    label: "AND",
    description: "All conditions must match",
  },
  {
    value: "or",
    label: "OR",
    description: "Any condition must match",
  },
  {
    value: "not",
    label: "NOT",
    description: "Condition must not match",
  },
];

/**
 * UI state for rule builder
 */
export interface RuleBuilderState {
  rule: TargetingRuleV1 | null;
  errors: ValidationError[];
  isDirty: boolean;
  isValidating: boolean;
  isPreviewing: boolean;
}
